import { useCallback, useRef, useState } from "react";
import { MoveHorizontal } from "lucide-react";

// Swap photos by changing `before` / `after` props (see PROJECTS in data/site.js).
export const BeforeAfter = ({ before, after, place, scope, duration, placeholder, testId = "before-after-slider", tall = false }) => {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);

  const move = useCallback((clientX) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    setPos(Math.min(100, Math.max(0, ((clientX - r.left) / r.width) * 100)));
  }, []);

  const onDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    move(e.clientX);
  };

  return (
    <figure className="group" data-testid={testId}>
      <div
        ref={ref}
        className={`relative w-full cursor-ew-resize select-none overflow-hidden rounded-sm bg-navy touch-pan-y ${tall ? "aspect-[4/5] sm:aspect-[4/3]" : "aspect-[4/3] sm:aspect-[16/10]"}`}
        onPointerDown={onDown}
        onPointerMove={(e) => e.buttons === 1 && move(e.clientX)}
      >
        <img src={after} alt={`After: ${scope} in ${place}`} className="absolute inset-0 h-full w-full object-cover" draggable="false" loading="lazy" />
        <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
          <img src={before} alt={`Before: ${scope} in ${place}`} className="absolute inset-0 h-full w-full object-cover" draggable="false" loading="lazy" />
        </div>
        <span className="absolute left-4 top-4 rounded-full bg-navy/85 px-3 py-1 font-mono text-[11px] font-semibold uppercase tracking-widest text-white">Before</span>
        <span className="absolute right-4 top-4 rounded-full bg-flame px-3 py-1 font-mono text-[11px] font-semibold uppercase tracking-widest text-navy">After</span>
        {placeholder && (
          <span className="absolute bottom-4 left-4 rounded-full bg-white/90 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-widest text-navy">Sample project</span>
        )}
        <div className="absolute inset-y-0 w-0.5 bg-white" style={{ left: `${pos}%` }}>
          <div className="absolute left-1/2 top-1/2 grid h-12 w-12 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border-2 border-white bg-flame text-navy shadow-xl transition-transform group-hover:scale-110">
            <MoveHorizontal className="h-5 w-5" />
          </div>
        </div>
        <input
          type="range" min="0" max="100" value={pos} onChange={(e) => setPos(Number(e.target.value))}
          aria-label={`Before and after comparison, ${place}`} data-testid={`${testId}-range`}
          className="absolute inset-0 h-full w-full cursor-ew-resize opacity-0"
        />
      </div>
      <figcaption className="mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-display text-lg font-bold text-navy">{place}</span>
        <span className="font-mono text-xs uppercase tracking-widest text-slate-600">{scope} · {duration}</span>
      </figcaption>
    </figure>
  );
};
